/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  FormControlLabel,
  FormControl,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { aiScheduleApi } from '../../services/api';
import { useAiDateTimeFormatting } from '../../hooks/useAiDateTimeFormatting';
import type { AISchedule, AIScheduleCreate, ScheduleType, RecurringUnit, WeekDay } from '../../types';

interface SchedulePanelProps {
  systemId: number;
}

interface ScheduleFormData {
  type: ScheduleType;
  start_at: string;
  end_at: string | null;
  interval_period: RecurringUnit | null;
  interval_every: number | null;
  excluded_days: WeekDay[] | null;
  excluded_time_ranges: { start: string; end: string }[] | null;
  is_active: boolean;
}

export const SchedulePanel: React.FC<SchedulePanelProps> = ({ systemId }) => {
  const queryClient = useQueryClient();
  const { formatDateTime, toDateTimeLocalValue, fromDateTimeLocalValue } = useAiDateTimeFormatting();
  const [formOpen, setFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<AISchedule | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<AISchedule | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const [formData, setFormData] = useState<ScheduleFormData>({
    type: 'ONE_TIME',
    start_at: '',
    end_at: null,
    interval_period: null,
    interval_every: null,
    excluded_days: null,
    excluded_time_ranges: null,
    is_active: true,
  });

  // Fetch schedules for this AI system
  const { data: schedules, isLoading } = useQuery({
    queryKey: ['schedules', systemId],
    queryFn: () => aiScheduleApi.list({ ai_system: systemId }),
    enabled: !!systemId,
  });

  // Create schedule mutation
  const createMutation = useMutation({
    mutationFn: (data: AIScheduleCreate) => aiScheduleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', systemId] });
      handleCloseForm();
    },
  });

  // Update schedule mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AIScheduleCreate> }) =>
      aiScheduleApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', systemId] });
      handleCloseForm();
    },
  });

  // Delete schedule mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => aiScheduleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', systemId] });
      setDeleteConfirmOpen(false);
      setScheduleToDelete(null);
    },
  });

  // Activate/deactivate schedule mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      aiScheduleApi.update(id, { is_active }),
    onMutate: async ({ id, is_active }) => {
      await queryClient.cancelQueries({ queryKey: ['schedules', systemId] });
      const previous = queryClient.getQueryData(['schedules', systemId]);
      queryClient.setQueryData(['schedules', systemId], (old: any) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((s: AISchedule) =>
            s.id === id ? { ...s, is_active } : s
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['schedules', systemId], context.previous);
      }
      setTogglingId(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', systemId] });
      setTogglingId(null);
    },
  });

  const handleOpenForm = (schedule?: AISchedule) => {
    if (schedule) {
      setEditingSchedule(schedule);
      
      console.log('Loading schedule for editing:', schedule);
      console.log('interval_every:', schedule.interval_every);
      console.log('interval_period:', schedule.interval_period);
      
      setFormData({
        type: schedule.type,
        start_at: toDateTimeLocalValue(schedule.start_at),
        end_at: schedule.end_at ? toDateTimeLocalValue(schedule.end_at) : null,
        interval_period: schedule.interval_period || null,
        interval_every: schedule.interval_every || null,
        excluded_days: schedule.excluded_days || null,
        excluded_time_ranges: schedule.excluded_time_ranges || null,
        is_active: schedule.is_active,
      });
    } else {
      setEditingSchedule(null);
      setFormData({
        type: 'ONE_TIME',
        start_at: '',
        end_at: null,
        interval_period: null,
        interval_every: null,
        excluded_days: null,
        excluded_time_ranges: null,
        is_active: true,
      });
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingSchedule(null);
  };

  const handleSubmit = () => {
    const scheduleData: AIScheduleCreate = {
      ai_system: systemId,
      type: formData.type,
      start_at: fromDateTimeLocalValue(formData.start_at),
      end_at: fromDateTimeLocalValue(formData.end_at),
      interval_period: formData.interval_period,
      interval_every: formData.interval_every,
      excluded_days: formData.excluded_days,
      excluded_time_ranges: formData.excluded_time_ranges,
      is_active: formData.is_active,
    };

    if (editingSchedule) {
      updateMutation.mutate({ id: editingSchedule.id, data: scheduleData });
    } else {
      createMutation.mutate(scheduleData);
    }
  };

  const handleDelete = (schedule: AISchedule) => {
    setScheduleToDelete(schedule);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (scheduleToDelete) {
      deleteMutation.mutate(scheduleToDelete.id);
    }
  };

  const handleToggleActive = (schedule: AISchedule) => {
    setTogglingId(schedule.id);
    toggleActiveMutation.mutate({
      id: schedule.id,
      is_active: !schedule.is_active,
    });
  };

  const formatRecurring = (schedule: AISchedule) => {
    if (schedule.type === 'ONE_TIME') {
      return 'One-time execution';
    }
    if (schedule.interval_every && schedule.interval_period) {
      return `Every ${schedule.interval_every} ${schedule.interval_period.toLowerCase()}`;
    }
    return 'Recurring';
  };

  const now = toDateTimeLocalValue(new Date());

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <ScheduleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Execution Schedules
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
          >
            Add Schedule
          </Button>
        </Box>

        {isLoading ? (
          <Typography>Loading schedules...</Typography>
        ) : schedules && schedules.results && schedules.results.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.results.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell>
                      <Chip
                        label={schedule.type}
                        size="small"
                        color={schedule.type === 'RECURRING' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(schedule.start_at)}</TableCell>
                    <TableCell>{formatDateTime(schedule.end_at)}</TableCell>
                    <TableCell>{formatRecurring(schedule)}</TableCell>
                    <TableCell>
                      <Chip
                        label={schedule.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={schedule.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(schedule)}
                        color={schedule.is_active ? 'warning' : 'success'}
                        disabled={togglingId === schedule.id}
                      >
                        {togglingId === schedule.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          schedule.is_active ? <PauseIcon /> : <PlayIcon />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenForm(schedule)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(schedule)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            No schedules configured. Click "Add Schedule" to create automated executions.
          </Alert>
        )}

        {/* Schedule Form Dialog */}
        <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label="Schedule Type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as ScheduleType })}
                >
                  <MenuItem value="ONE_TIME">One-Time Execution</MenuItem>
                  <MenuItem value="RECURRING">Recurring Execution</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="datetime-local"
                  value={formData.start_at}
                  onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: now }}
                />
              </Grid>

              {formData.type === 'RECURRING' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="End Time (Optional)"
                    type="datetime-local"
                    value={formData.end_at || ''}
                    onChange={(e) => setFormData({ ...formData, end_at: e.target.value || null })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: formData.start_at || now }}
                  />
                </Grid>
              )}

              {formData.type === 'RECURRING' && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Repeat Every"
                      value={formData.interval_every || ''}
                      onChange={(e) => setFormData({ ...formData, interval_every: parseInt(e.target.value) || null })}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      select
                      fullWidth
                      label="Time Unit"
                      value={formData.interval_period || ''}
                      onChange={(e) => setFormData({ ...formData, interval_period: e.target.value as RecurringUnit })}
                      SelectProps={{
                        displayEmpty: true,
                      }}
                    >
                      <MenuItem value="">Select Time Unit</MenuItem>
                      <MenuItem value="seconds">Seconds</MenuItem>
                      <MenuItem value="minutes">Minutes</MenuItem>
                      <MenuItem value="hours">Hours</MenuItem>
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="weeks">Weeks</MenuItem>
                    </TextField>
                  </Grid>
                </>
              )}

              {formData.type === 'RECURRING' && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Exclude Days (Optional)"
                      SelectProps={{
                        multiple: true,
                        value: formData.excluded_days || [],
                        onChange: (e) => setFormData({ 
                          ...formData, 
                          excluded_days: (e.target.value as WeekDay[]).length > 0 ? (e.target.value as WeekDay[]) : null 
                        }),
                        renderValue: (selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as WeekDay[]).map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        ),
                      }}
                    >
                      <MenuItem value="MONDAY">Monday</MenuItem>
                      <MenuItem value="TUESDAY">Tuesday</MenuItem>
                      <MenuItem value="WEDNESDAY">Wednesday</MenuItem>
                      <MenuItem value="THURSDAY">Thursday</MenuItem>
                      <MenuItem value="FRIDAY">Friday</MenuItem>
                      <MenuItem value="SATURDAY">Saturday</MenuItem>
                      <MenuItem value="SUNDAY">Sunday</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>
              )}

              {formData.type === 'RECURRING' && (
                <Grid size={{ xs: 12 }}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Exclude Time Ranges (Optional)
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newRanges = [...(formData.excluded_time_ranges || []), { start: '00:00', end: '00:00' }];
                          setFormData({ ...formData, excluded_time_ranges: newRanges });
                        }}
                      >
                        Add Time Range
                      </Button>
                    </Box>
                    
                    {formData.excluded_time_ranges && formData.excluded_time_ranges.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {formData.excluded_time_ranges.map((range, index) => (
                          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              type="time"
                              label="Start Time"
                              value={range.start}
                              onChange={(e) => {
                                const newRanges = [...formData.excluded_time_ranges!];
                                newRanges[index] = { ...newRanges[index], start: e.target.value };
                                setFormData({ ...formData, excluded_time_ranges: newRanges });
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <Typography variant="body2" color="text.secondary">to</Typography>
                            <TextField
                              size="small"
                              type="time"
                              label="End Time"
                              value={range.end}
                              onChange={(e) => {
                                const newRanges = [...formData.excluded_time_ranges!];
                                newRanges[index] = { ...newRanges[index], end: e.target.value };
                                setFormData({ ...formData, excluded_time_ranges: newRanges });
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                const newRanges = formData.excluded_time_ranges!.filter((_, i) => i !== index);
                                setFormData({ ...formData, excluded_time_ranges: newRanges.length > 0 ? newRanges : null });
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {(!formData.excluded_time_ranges || formData.excluded_time_ranges.length === 0) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        No time ranges excluded. Click "Add Time Range" to exclude specific time periods (e.g., overnight 22:00-06:00).
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active (start executing immediately)"
                />
              </Grid>

              {formData.type === 'RECURRING' && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Recurring Schedule:</strong> The system will execute every{' '}
                      {formData.interval_every || 'X'} {formData.interval_period?.toLowerCase() || 'unit(s)'}
                      {formData.end_at && ` until ${formatDateTime(formData.end_at)}`}.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                !formData.start_at ||
                (formData.type === 'RECURRING' && (!formData.interval_period || !formData.interval_every)) ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {editingSchedule ? 'Update' : 'Create'} Schedule
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this schedule? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
